import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Sparkles, Shield, Zap, Search, BarChart3, PenTool, Check, ArrowRight, Users, Building2, Scale } from 'lucide-react';

// --- Animated counter ---
function AnimatedNumber({ target, duration = 1500, prefix = '', suffix = '' }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        let start = 0;
        const step = target / (duration / 16);
        const tick = () => {
          start = Math.min(start + step, target);
          setValue(Math.round(start));
          if (start < target) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{prefix}{value}{suffix}</span>;
}

// --- Scroll-reveal wrapper ---
function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(30px)',
      transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
    }}>
      {children}
    </div>
  );
}

// --- Animated score demo ---
function ScoreDemo() {
  const [score, setScore] = useState(82);
  const [phase, setPhase] = useState('before');
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => setPhase('scanning'), 800);
        setTimeout(() => { setPhase('after'); setScore(14); }, 2800);
        setTimeout(() => { setPhase('before'); setScore(82); }, 6000);
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const color = phase === 'after' ? '#10b981' : '#ef4444';
  const label = phase === 'after' ? 'LOW RISK' : 'HIGH RISK';
  const r = 38;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;

  return (
    <div ref={ref} style={{
      display: 'flex', alignItems: 'center', gap: 24, justifyContent: 'center',
    }}>
      <div style={{
        background: '#141419', border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 16, padding: '24px 32px', textAlign: 'center',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        transition: 'all 0.5s',
      }}>
        <div style={{ color: '#52525b', fontSize: 11, fontWeight: 600, marginBottom: 12, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {phase === 'scanning' ? 'Scanning...' : phase === 'after' ? 'After' : 'Before'}
        </div>
        <div style={{ position: 'relative', width: 100, height: 100, margin: '0 auto 12px' }}>
          <svg width="100" height="100" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
            <circle cx="50" cy="50" r={r} fill="none" stroke={phase === 'scanning' ? '#2563eb' : color}
              strokeWidth="6" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
              transform="rotate(-90 50 50)"
              style={{ transition: 'stroke-dashoffset 1s ease, stroke 0.5s' }}
              className={phase === 'scanning' ? 'score-ring-scanning' : ''}
            />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{
              color: phase === 'scanning' ? '#2563eb' : color,
              fontSize: 28, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace",
              transition: 'color 0.5s',
            }}>{score}</span>
          </div>
        </div>
        <span style={{
          color: phase === 'scanning' ? '#2563eb' : color,
          fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
          transition: 'color 0.5s',
        }}>
          {phase === 'scanning' ? 'ANALYZING' : label}
        </span>
      </div>
    </div>
  );
}

// --- Pipeline step ---
function PipelineStep({ icon, title, desc, color, delay, isLast }) {
  return (
    <Reveal delay={delay}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        <div style={{
          background: '#141419', border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 14, padding: '20px 24px', textAlign: 'center',
          minWidth: 180, boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
          onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.3)'; }}
          onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)'; }}
        >
          <div style={{
            width: 44, height: 44, borderRadius: 12, margin: '0 auto 12px',
            background: `${color}15`, border: `1px solid ${color}25`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {icon}
          </div>
          <div style={{ color: '#e4e4e7', fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{title}</div>
          <div style={{ color: '#71717a', fontSize: 12, lineHeight: 1.5 }}>{desc}</div>
        </div>
        {!isLast && (
          <div className="pipeline-arrow" style={{ padding: '0 8px', color: '#3f3f46', fontSize: 20 }}>→</div>
        )}
      </div>
    </Reveal>
  );
}

// ============================================
// MAIN LANDING COMPONENT
// ============================================
export default function LandingHero({ onEnterApp, onQuickAnalyze }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [typedText, setTypedText] = useState('');

  // Typing effect for headline
  const fullText = 'Un-vague your requirements';
  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      setTypedText(fullText.slice(0, i + 1));
      i++;
      if (i >= fullText.length) clearInterval(timer);
    }, 50);
    return () => clearInterval(timer);
  }, []);

  const handleAnalyze = async () => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    setLoading(true);
    try { await onQuickAnalyze(trimmed); }
    catch { setLoading(false); }
  };

  return (
    <div style={{
      background: '#09090b',
      backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)',
      backgroundSize: '28px 28px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      overflowX: 'hidden',
    }}>

      {/* ===== HERO SECTION ===== */}
      <section style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {/* Nav */}
        <nav style={{
          width: '100%', maxWidth: 1200, margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', position: 'relative', zIndex: 2,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/logo-uv.png" alt="UV" style={{ width: 32, height: 32, borderRadius: 8 }} />
            <span style={{ color: '#f4f4f5', fontWeight: 800, fontSize: 20, letterSpacing: '-0.03em' }}>UnVague</span>
          </div>
          <button onClick={onEnterApp} style={{
            background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', border: 'none',
            color: '#fff', fontSize: 13, fontWeight: 600, padding: '8px 20px',
            borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
            boxShadow: '0 2px 10px rgba(37,99,235,0.3)',
          }}
            onMouseOver={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(37,99,235,0.5)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseOut={e => { e.currentTarget.style.boxShadow = '0 2px 10px rgba(37,99,235,0.3)'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Open App
          </button>
        </nav>

        {/* Hero content */}
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: '0 24px', maxWidth: 900, width: '100%',
          margin: '0 auto', marginTop: -40,
        }}>
          {/* Badge */}
          <Reveal>
            <div style={{
              background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.2)',
              borderRadius: 20, padding: '6px 16px', marginBottom: 28,
            }}>
              <span style={{ color: '#3b82f6', fontSize: 13, fontWeight: 600 }}>Requirements Ambiguity Engine</span>
            </div>
          </Reveal>

          {/* Headline with typing effect */}
          <h1 className="hero-headline" style={{
            color: '#f4f4f5', fontSize: 56, fontWeight: 800, textAlign: 'center',
            lineHeight: 1.1, letterSpacing: '-0.03em', margin: '0 0 8px', minHeight: '1.2em',
          }}>
            <span className="gradient-text">{typedText}</span>
            <span className="typing-cursor" style={{ color: '#2563eb', animation: 'blink 1s infinite' }}>|</span>
          </h1>
          <h2 style={{
            color: '#71717a', fontSize: 20, fontWeight: 400, textAlign: 'center',
            margin: '0 0 40px', lineHeight: 1.5,
          }}>
            before they cost you
          </h2>

          {/* Subheadline */}
          <Reveal delay={0.2}>
            <p style={{
              color: '#71717a', fontSize: 17, textAlign: 'center', maxWidth: 600,
              lineHeight: 1.7, margin: '0 auto 36px',
            }}>
              Paste any Jira ticket, PRD, or spec. 3 AI agents scan for ambiguity,
              score it 0-100, and rewrite it clearly — in seconds.
            </p>
          </Reveal>

          {/* Inline textarea */}
          <Reveal delay={0.3}>
            <div style={{ width: '100%', maxWidth: 580 }}>
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder='Try it — paste a vague requirement here...'
                rows={3}
                style={{
                  width: '100%', background: '#0d0d12', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 12, padding: '14px 16px', color: '#f4f4f5', fontSize: 14,
                  fontFamily: 'inherit', resize: 'none', outline: 'none', lineHeight: 1.6,
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => { e.target.style.borderColor = 'rgba(37,99,235,0.5)'; }}
                onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }}
              />
              <button
                onClick={handleAnalyze}
                disabled={!text.trim() || loading}
                style={{
                  width: '100%', marginTop: 10, padding: '14px 24px',
                  background: text.trim() ? 'linear-gradient(135deg, #2563eb, #1d4ed8)' : '#1a1a2e',
                  color: text.trim() ? '#fff' : '#52525b',
                  border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700,
                  cursor: text.trim() && !loading ? 'pointer' : 'default',
                  fontFamily: 'inherit',
                  boxShadow: text.trim() ? '0 4px 20px rgba(37,99,235,0.4)' : 'none',
                  transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                {loading ? (
                  <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Analyzing...</>
                ) : (
                  <><Sparkles size={16} /> Analyze Now — Free</>
                )}
              </button>
            </div>
          </Reveal>
        </div>

        {/* Scroll hint */}
        <div className="scroll-bounce" style={{ textAlign: 'center', padding: '24px 0', color: '#3f3f46' }}>
          <ChevronDown size={24} />
        </div>
      </section>

      {/* ===== STATS BAR ===== */}
      <section style={{
        borderTop: '1px solid rgba(255,255,255,0.04)',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        background: 'rgba(255,255,255,0.02)',
        padding: '40px 24px',
      }}>
        <div style={{
          maxWidth: 900, margin: '0 auto',
          display: 'flex', justifyContent: 'center', gap: 48, flexWrap: 'wrap',
        }}>
          {[
            { value: 85, prefix: '$', suffix: 'B/yr', label: 'rework from bad requirements' },
            { value: 17, suffix: ' hrs/wk', label: 'wasted on unclear specs' },
            { value: 3, suffix: ' agents', label: 'detect, assess, rewrite' },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.15}>
              <div style={{ textAlign: 'center', minWidth: 160 }}>
                <div style={{ color: '#2563eb', fontSize: 36, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 4 }}>
                  <AnimatedNumber target={s.value} prefix={s.prefix || ''} suffix={s.suffix} />
                </div>
                <div style={{ color: '#71717a', fontSize: 13 }}>{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2 style={{ color: '#f4f4f5', fontSize: 32, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 12px' }}>
                How It Works
              </h2>
              <p style={{ color: '#71717a', fontSize: 16, maxWidth: 500, margin: '0 auto' }}>
                Three specialized AI agents work in sequence to eliminate ambiguity
              </p>
            </div>
          </Reveal>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexWrap: 'wrap', gap: 0 }}>
            <PipelineStep icon={<Search size={22} color="#22d3ee" />} title="Detect" desc="Scans for vague, ambiguous phrases" color="#22d3ee" delay={0.1} />
            <PipelineStep icon={<BarChart3 size={22} color="#f59e0b" />} title="Assess" desc="Scores across 20 quality dimensions" color="#f59e0b" delay={0.25} />
            <PipelineStep icon={<PenTool size={22} color="#10b981" />} title="Rewrite" desc="Generates clear, testable specs" color="#10b981" delay={0.4} isLast />
          </div>

          {/* Animated score demo */}
          <div style={{ marginTop: 56 }}>
            <Reveal delay={0.2}>
              <ScoreDemo />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== WHO IT'S FOR ===== */}
      <section style={{
        padding: '80px 24px',
        background: 'rgba(255,255,255,0.015)',
        borderTop: '1px solid rgba(255,255,255,0.04)',
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2 style={{ color: '#f4f4f5', fontSize: 32, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 12px' }}>
                Built For Teams That Ship
              </h2>
              <p style={{ color: '#71717a', fontSize: 16 }}>
                Catch vague requirements before they become expensive bugs
              </p>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
            {[
              { icon: <Users size={24} color="#2563eb" />, title: 'Engineering Managers', desc: 'Catch vague tickets before sprint planning — saves 40+ hours per cycle', color: '#2563eb' },
              { icon: <Building2 size={24} color="#22d3ee" />, title: 'Government & Policy', desc: 'Analyze circulars and directives for implementation gaps before rollout', color: '#22d3ee' },
              { icon: <Scale size={24} color="#f59e0b" />, title: 'Compliance & Legal', desc: 'Flag ambiguous contract clauses and SLA language before signing', color: '#f59e0b' },
            ].map((p, i) => (
              <Reveal key={i} delay={i * 0.15}>
                <div style={{
                  background: '#141419', border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 14, padding: '24px',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                  onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 8px 32px rgba(0,0,0,0.3)`; }}
                  onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{
                    width: 48, height: 48, borderRadius: 12, marginBottom: 16,
                    background: `${p.color}10`, border: `1px solid ${p.color}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {p.icon}
                  </div>
                  <div style={{ color: '#e4e4e7', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{p.title}</div>
                  <div style={{ color: '#71717a', fontSize: 13, lineHeight: 1.6 }}>{p.desc}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section style={{ padding: '80px 24px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2 style={{ color: '#f4f4f5', fontSize: 32, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 12px' }}>
                Built for Teams, Priced for Scale
              </h2>
              <p style={{ color: '#71717a', fontSize: 16 }}>Start free. Scale to thousands of stories.</p>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {/* Free tier */}
            <Reveal delay={0.1}>
              <div style={{
                background: '#141419', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 16, padding: '28px 22px', height: '100%', display: 'flex', flexDirection: 'column',
              }}>
                <div style={{ color: '#a1a1aa', fontSize: 13, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>Free</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 6 }}>
                  <span style={{ color: '#f4f4f5', fontSize: 36, fontWeight: 800 }}>$0</span>
                  <span style={{ color: '#52525b', fontSize: 13 }}>/month</span>
                </div>
                <div style={{ color: '#71717a', fontSize: 13, marginBottom: 20 }}>Try it out</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24, flex: 1 }}>
                  {['10 scans per month', '20-dimension scoring', 'INCOSE, IEEE, SMART standards', '3-agent pipeline + rewrite', 'Export to markdown'].map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <Check size={13} color="#10b981" style={{ flexShrink: 0, marginTop: 2 }} />
                      <span style={{ color: '#a1a1aa', fontSize: 12.5 }}>{f}</span>
                    </div>
                  ))}
                </div>
                <button onClick={onEnterApp} style={{
                  width: '100%', padding: '11px', background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10,
                  color: '#e4e4e7', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                  transition: 'all 0.15s',
                }}
                  onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                  onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                >
                  Get Started Free
                </button>
              </div>
            </Reveal>

            {/* Pro tier */}
            <Reveal delay={0.2}>
              <div style={{
                background: '#141419', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 16, padding: '28px 22px', position: 'relative', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column',
              }}>
                <div style={{
                  position: 'absolute', top: 12, right: 12,
                  background: '#2563eb', color: '#fff', fontSize: 9, fontWeight: 700,
                  padding: '2px 8px', borderRadius: 5, letterSpacing: '0.04em',
                }}>COMING SOON</div>
                <div style={{ color: '#a1a1aa', fontSize: 13, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>Pro</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 6 }}>
                  <span style={{ color: '#f4f4f5', fontSize: 36, fontWeight: 800 }}>$49</span>
                  <span style={{ color: '#52525b', fontSize: 13 }}>/user/month</span>
                </div>
                <div style={{ color: '#71717a', fontSize: 13, marginBottom: 20 }}>For individual PMs & analysts</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24, flex: 1 }}>
                  {['500 scans per month', 'Everything in Free', 'API access (JSON)', 'Priority support', 'CSV/PDF export'].map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <Check size={13} color="#a1a1aa" style={{ flexShrink: 0, marginTop: 2 }} />
                      <span style={{ color: '#a1a1aa', fontSize: 12.5 }}>{f}</span>
                    </div>
                  ))}
                </div>
                <button style={{
                  width: '100%', padding: '11px',
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10, color: '#71717a', fontSize: 13, fontWeight: 600,
                  cursor: 'default', fontFamily: 'inherit', opacity: 0.7,
                }}>
                  Coming Soon
                </button>
              </div>
            </Reveal>

            {/* Team tier — highlighted */}
            <Reveal delay={0.3}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(37,99,235,0.08) 0%, rgba(37,99,235,0.03) 100%)',
                border: '1px solid rgba(37,99,235,0.25)',
                borderRadius: 16, padding: '28px 22px', position: 'relative', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column',
              }}>
                <div style={{
                  position: 'absolute', top: 12, right: 12,
                  background: '#2563eb', color: '#fff', fontSize: 9, fontWeight: 700,
                  padding: '2px 8px', borderRadius: 5, letterSpacing: '0.04em',
                }}>COMING SOON</div>
                <div style={{ color: '#3b82f6', fontSize: 13, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>Team</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 6 }}>
                  <span style={{ color: '#f4f4f5', fontSize: 36, fontWeight: 800 }}>$99</span>
                  <span style={{ color: '#52525b', fontSize: 13 }}>/user/month</span>
                </div>
                <div style={{ color: '#71717a', fontSize: 13, marginBottom: 20 }}>For teams up to 25 users</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24, flex: 1 }}>
                  {['Unlimited scans', 'Everything in Pro', 'Jira integration', 'Team dashboard & trends', 'Cross-ticket analysis', 'Compliance packs'].map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <Check size={13} color="#3b82f6" style={{ flexShrink: 0, marginTop: 2 }} />
                      <span style={{ color: '#a1a1aa', fontSize: 12.5 }}>{f}</span>
                    </div>
                  ))}
                </div>
                <button style={{
                  width: '100%', padding: '11px',
                  background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                  border: 'none', borderRadius: 10,
                  color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'default', fontFamily: 'inherit',
                  boxShadow: '0 4px 16px rgba(37,99,235,0.3)', opacity: 0.7,
                }}>
                  Coming Soon
                </button>
              </div>
            </Reveal>

            {/* Enterprise tier */}
            <Reveal delay={0.4}>
              <div style={{
                background: '#141419', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 16, padding: '28px 22px', height: '100%', display: 'flex', flexDirection: 'column',
              }}>
                <div style={{ color: '#a1a1aa', fontSize: 13, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>Enterprise</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 6 }}>
                  <span style={{ color: '#f4f4f5', fontSize: 36, fontWeight: 800 }}>Custom</span>
                </div>
                <div style={{ color: '#71717a', fontSize: 13, marginBottom: 20 }}>Unlimited users & stories</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24, flex: 1 }}>
                  {['Everything in Team', 'Unlimited users', 'SSO (SAML/OIDC)', 'SOC 2 compliance', 'Dedicated support & SLA', 'Custom compliance packs'].map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <Check size={13} color="#a1a1aa" style={{ flexShrink: 0, marginTop: 2 }} />
                      <span style={{ color: '#a1a1aa', fontSize: 12.5 }}>{f}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => window.location.href = 'mailto:manchitsanan@gmail.com?subject=UnVague Enterprise Inquiry'} style={{
                  width: '100%', padding: '11px',
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10, color: '#e4e4e7', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                }}
                  onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                  onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                >
                  Contact Sales
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section style={{
        padding: '80px 24px',
        background: 'linear-gradient(135deg, rgba(37,99,235,0.06) 0%, rgba(34,211,238,0.03) 100%)',
        borderTop: '1px solid rgba(37,99,235,0.1)',
        textAlign: 'center',
      }}>
        <Reveal>
          <h2 style={{ color: '#f4f4f5', fontSize: 32, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 12px' }}>
            Stop shipping ambiguity
          </h2>
          <p style={{ color: '#71717a', fontSize: 16, maxWidth: 450, margin: '0 auto 32px', lineHeight: 1.6 }}>
            Paste your first requirement. See exactly what's vague — and get a rewrite in seconds.
          </p>
          <button onClick={onEnterApp} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', border: 'none',
            borderRadius: 10, padding: '14px 32px', color: '#fff', fontSize: 16, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
            boxShadow: '0 4px 20px rgba(37,99,235,0.4)', transition: 'all 0.2s',
          }}
            onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(37,99,235,0.5)'; }}
            onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,99,235,0.4)'; }}
          >
            Try UnVague Free <ArrowRight size={18} />
          </button>
        </Reveal>
      </section>

      {/* ===== FOOTER ===== */}
      <footer style={{
        padding: '24px', textAlign: 'center',
        borderTop: '1px solid rgba(255,255,255,0.04)',
        color: '#3f3f46', fontSize: 12,
      }}>
        UnVague — Requirements Ambiguity Engine
      </footer>
    </div>
  );
}
